import React from "react";
import { useTheme, useMediaQuery, Stack, Typography } from "@mui/material";

import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Legend, Line, LabelList } from 'recharts';
import { CurveType } from "recharts/types/shape/Curve";
import { HorizontalAlignmentType, VerticalAlignmentType } from "recharts/types/component/DefaultLegendContent";

import { renderTrendEvent } from "./TrendChart";
import { TrendChartSettingsStateType, defaultSettings } from "./TrendChartSettings";
import TitleCase from "components/formatting/TitleCase";

interface TrendChartGraphPreviewProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: TrendChartSettingsStateType | {};
}

export default function TrendChartGraphPreview(props: TrendChartGraphPreviewProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const currentState = {...defaultSettings, ...props.settingsState};

  const legendWrapperStyle = currentState.verticalAlign === "top" ? { top: -10 } : { bottom: 5 };
  const windowHeight = window.innerHeight;

  const dummyData = [
    {
      value: 55440,
      name: "Value 1",
      label: "100"
    },
    {
      value: 49373,
      name: "Value 2",
      label: "300",
      value_trendEvent: {
        fillColor: currentState.lineColor,
        showEvent: true,
        textColor: theme.palette.common.white
      }
    },
    {
      value: 48873,
      name: "Value 3",
      label: "270"
    },
    {
      value: 41172,
      name: "Value 4",
      label: "350"
    },
    {
      value: 12214,
      name: "Value 5",
      label: "500",
      value_trendEvent: {
        fillColor: currentState.lineColor,
        showEvent: true,
        textColor: theme.palette.common.white
      }
    }
  ];

  return (
    <Stack
      sx={{
        gap: 3,
        width: isSmallScreen ? "100%" : "50%",
        flex: 1,
        height: windowHeight < 600 ? "35vh" : "55vh"
      }}>
      {currentState.widgetTitle !== 'none' &&
        <Typography
          sx={{ fontSize: "1.2rem", textAlign: `${currentState.widgetTitleAlignment}` }}
          variant="subtitle2">
          <TitleCase text={`${currentState.widgetTitle} Title`} />
        </Typography>}
      <ResponsiveContainer
        width="100%"
        aspect={windowHeight < 600 ? 0 : 1}
        className={isSmallScreen ? "mobile" : ""}
        >
        <LineChart
          data={dummyData}>
          {currentState.showGrid && <CartesianGrid strokeDasharray="3 3" />}
          {currentState.xAxis && <XAxis dataKey="name" />}
          {currentState.yAxis && <YAxis />}
          <Line
            type={currentState.lineType as CurveType}
            strokeWidth={currentState.lineWidth}
            stroke={currentState.lineColor}
            dataKey="label"
            dot={currentState.showDots}
            isAnimationActive={false}
          >
            {currentState.showEvents && 
              <LabelList
                dataKey="value_trendEvent"
                content={renderTrendEvent} />
            }
          </Line>
          {currentState.showLegend &&
            <Legend
              wrapperStyle={legendWrapperStyle}
              align={currentState.horizontalAlign as HorizontalAlignmentType}
              verticalAlign={currentState.verticalAlign?.toLowerCase() as VerticalAlignmentType} />
          }
        </LineChart>
      </ResponsiveContainer>
    </Stack>
  );
}
